import i18next, { type LanguageDetectorModule, type i18n as I18nInstance } from "i18next";
import { initReactI18next } from "react-i18next";
import { env } from "@/app/config/env";
import { log } from "@/app/observability";
import { preferences } from "@/app/platform";

/**
 * TRANSLATIONS FIND THEMSELVES.
 *
 * Every feature keeps its own strings next to its code (`features/<domain>/locales/<lang>/
 * <namespace>.json`) and app-wide strings live in `app/locales`. This glob collects them at
 * build time, so adding a feature never means editing a central registry — the step people
 * forget, and whose symptom is a screen of raw keys in production.
 */
const modules = import.meta.glob<Record<string, unknown>>(
  ["../locales/*/*.json", "../../features/**/locales/*/*.json", "../../shared/**/locales/*/*.json"],
  { eager: true, import: "default" },
);

const resources: Record<string, Record<string, unknown>> = {};

for (const [path, contents] of Object.entries(modules)) {
  const match = path.match(/locales\/([^/]+)\/([^/]+)\.json$/);
  if (!match) continue;
  const [, language, namespace] = match;
  resources[language] ??= {};
  resources[language][namespace] = contents;
}

/**
 * WHERE THE CHOSEN LANGUAGE IS REMEMBERED, AND WHY IT IS NOT THE WEB VIEW'S OWN STORE.
 *
 * `i18next-browser-languagedetector` kept it in `localStorage`. Inside Capacitor that is
 * WKWebView's store, and WebKit is allowed to EVICT it when the device runs short of space —
 * the same eviction the query cache was moved off for, argued at length in
 * `app/platform/preferences.ts` and again in `app/query/query-persistence.ts`. The language
 * was the one thing left behind. Nothing breaks when it goes and nothing is logged: someone
 * who deliberately chose German opens the app one morning and it is in English, with no way
 * to tell anyone why.
 *
 * `app/platform/preferences.ts` is `UserDefaults` on device, which the system does not
 * reclaim. A language is exactly what that store is for — small, not a secret, and it has to
 * still be there tomorrow morning.
 *
 * The detector below is what replaced the dependency, and it is smaller than the package it
 * removes: this app needs none of what that package mostly is — a cookie, a querystring, a
 * subdomain, a path segment, an html `lang` attribute — none of which exist inside an app.
 */
export const LANGUAGE_KEY = "app.language";

/**
 * What the phone says, in preference order. `navigator.languages` is the ordered list set in
 * iOS Settings; `navigator.language` is the single best one, kept behind it for anything
 * that does not populate the list. i18next matches the first entry it supports, so `en-GB`
 * finds `en` without needing a folder of its own.
 */
function languagesFromDevice(): readonly string[] {
  const device: Navigator | undefined = globalThis.navigator;
  if (!device) return [];
  return [...(device.languages ?? []), device.language].filter(Boolean);
}

/**
 * THE ONE HARD PART: i18next ASKS SYNCHRONOUSLY, AND A DURABLE STORE ANSWERS LATER.
 *
 * `detect()` must return a language there and then. On device the preference store is a
 * bridge call into `UserDefaults`, so its answer arrives a tick or two after it is wanted.
 * Two ways out, and this takes the second:
 *
 *   1. AWAIT THE READ BEFORE i18next STARTS. Exact and simple, and it puts a round trip
 *      through the native bridge in front of the first React render — a cost paid on every
 *      launch, including every launch by someone who never changed their language.
 *   2. START FROM MEMORY, WRITE THROUGH, CORRECT WHEN THE READ LANDS. Nothing is held up:
 *      i18next initialises immediately with what the PHONE says, the stored choice is read
 *      alongside it, and the language changes once if the two differ.
 *
 * WHAT THE SECOND COSTS, HONESTLY: a window between init and that read landing in which the
 * app holds the device language rather than the chosen one. Nothing is on screen during it.
 * `main.tsx` renders `null` until the session is known, and the session is deliberately NOT
 * persisted (see the allow-list in `app/query/query-persistence.ts`), so knowing it always
 * costs a network round trip — this project budgets four seconds for one — against a local
 * read measured in milliseconds. On device the splash screen is up for all of it.
 *
 * AND THE TRAP INSIDE IT, which is what `hydrated` is for. i18next calls
 * `cacheUserLanguage()` for the language it resolves AT INIT, not only for one a person
 * picks — so a naive write-through would save the phone's language over the stored choice a
 * few milliseconds before reading it, and the choice would be gone for good. Hence: the read
 * is issued BEFORE the detector is handed over, and nothing reaches the store until it has
 * landed.
 */
function createDurableLanguageDetector() {
  /** Issued now, so it reads what was on disk BEFORE this launch's own detection runs. */
  const pending = preferences.get(LANGUAGE_KEY);

  let remembered: string | undefined;
  let hydrated = false;
  let detectedAtLaunch: string | undefined;

  const detector: LanguageDetectorModule = {
    type: "languageDetector",

    detect: () => remembered ?? languagesFromDevice(),

    cacheUserLanguage(language) {
      remembered = language;
      if (!hydrated) {
        // The launch-time echo rather than a choice: held in case nothing was stored yet.
        detectedAtLaunch = language;
        return;
      }
      void preferences.set(LANGUAGE_KEY, language);
    },
  };

  /**
   * Applies whatever was stored once the read lands, and only then opens the store to
   * writes.
   *
   * A stored code the build no longer offers is ignored rather than obeyed: a locale dropped
   * from `VITE_SUPPORTED_LOCALES` has no files left in the bundle, and honouring it would
   * render every key on every screen as its own dotted path.
   */
  async function applyStoredLanguage(instance: I18nInstance): Promise<void> {
    let stored: string | null;

    try {
      stored = await pending;
    } catch (error) {
      // The store is unreadable this launch. The device language is already in place, and
      // writing over a choice we failed to read would turn one bad launch into a permanent
      // loss — so this opens the store to writes and changes nothing else.
      //
      // AND IT IS REPORTED HERE rather than left to the caller's `catch`: swallowing the
      // rejection is the whole point of this block, so the promise `initI18n` guards never
      // rejects and the line would never be written. Somebody reading the app in the wrong
      // language needs this to be the difference between a mystery and a log entry.
      hydrated = true;
      log.warn("could not read the stored language, keeping the device's", { error });
      return;
    }

    hydrated = true;

    if (stored && env.locales.includes(stored)) {
      if (stored !== instance.resolvedLanguage) await instance.changeLanguage(stored);
      else remembered = stored;
      return;
    }

    // Nothing usable was stored, so this is a first launch: what the phone said becomes the
    // remembered answer, which is what the library this replaces did as well.
    if (detectedAtLaunch) await preferences.set(LANGUAGE_KEY, detectedAtLaunch);
  }

  return { detector, applyStoredLanguage };
}

/**
 * CALLED EXPLICITLY FROM main.tsx, never triggered by importing this module.
 *
 * A module that configures itself on import has to be imported for its side effect, and a
 * side-effect import exports nothing, binds nothing, and looks removable to any reader or
 * automated cleanup. When it is removed the app does not fail — it renders raw translation
 * keys. `no-side-effect-import` (scripts/eslint-rules/) makes that shape unavailable.
 *
 * THE INSTANCE IS AN ARGUMENT so that a launch can be staged more than once in one process.
 * i18next's default export is a singleton and there is no way to un-initialise it, so a test
 * that wants to watch a SECOND launch read back what the first one stored has no other way
 * to get one — and that second launch is the whole behaviour this file's storage choice is
 * about. The app always uses the default.
 */
export function initI18n(instance: I18nInstance = i18next) {
  if (instance.isInitialized) return instance;

  const language = createDurableLanguageDetector();

  void instance
  .use(language.detector)
  .use(initReactI18next)
  .init({
    resources: resources as never,
    fallbackLng: env.defaultLocale,
    supportedLngs: env.locales,
    defaultNS: "common",
    interpolation: { escapeValue: false },
  });

  // Best effort by design: a failure here leaves the app in the phone's language, which is a
  // working screen rather than a start-up crash — but it is not allowed to be silent.
  void language
    .applyStoredLanguage(instance)
    .catch((error: unknown) => log.warn("could not apply the stored language", { error }));

  return instance;
}

export { i18next };
